/**
 * TTSAudioPlayer.tsx - Compact TTS Audio Player for Chat Bubbles
 *
 * A small audio player component that can generate and play TTS audio
 * for chat messages. Appears as a button with audio controls when playing.
 */

import React, { useState, useRef, useEffect, useMemo, useCallback, useId } from 'react';
import { Box, HStack, IconButton, Text, Spinner } from '@chakra-ui/react';
import { LuPlay, LuPause, LuDownload } from 'react-icons/lu';
import { isLocalhost } from 'core';
import { useTTSConfig } from '@/contexts/ChatContext';
import { useTranslation } from 'react-i18next';
import { isMobileDevice } from '@/utilities/audioUnlock';
import { downloadUrl } from '@/utilities/downloadUrl';

const API_BASE =
  typeof window !== "undefined" && isLocalhost()
    ? "http://localhost:3002"
    : "";

interface TTSAudioPlayerProps {
    contentId: string;
    pageId?: string;
    contentType?: 'post' | 'message';
    textContent?: string; // Pass the text content for language detection
    shouldAutoPlay?: boolean; // Whether to auto-play when content becomes available
    isNewMessage?: boolean; // Whether this is a newly received message
}

interface TTSResponse {
    success: boolean;
    status?: 'success' | 'pending' | 'processing' | 'failed';
    jobId?: string;
    data?: {
        audioUrl: string;
        duration: number;
        cached: boolean;
    };
    error?: string;
}

// Poll an async TTS job until success/failure. Long-text generation can exceed
// proxy gateway timeouts, so the backend returns a jobId and we poll for it.
async function pollTtsJob(
    jobId: string,
    opts: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<TTSResponse> {
    const intervalMs = opts.intervalMs ?? 2000;
    const timeoutMs = opts.timeoutMs ?? 5 * 60 * 1000;
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
        await new Promise((r) => setTimeout(r, intervalMs));
        const res = await fetch(`${API_BASE}/ua/tts/status/${jobId}`, {
            credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const body: TTSResponse = await res.json();
        if (body.status === 'success') return body;
        if (body.status === 'failed') throw new Error(body.error || 'TTS generation failed');
    }
    throw new Error('TTS generation timed out');
}

// RTL languages
const RTL_LANGUAGES = ['ar', 'ckb', 'ur'];

// Language to TTS configuration mapping
const LANGUAGE_TTS_CONFIG: Record<string, { language: string; voice: string }> = {
    'ar': { language: 'ar-XA', voice: 'ar-XA-Standard-A' },
    'en': { language: 'en-US', voice: 'en-US-Neural2-A' },
    'en-US': { language: 'en-US', voice: 'en-US-Neural2-A' },
    'en-GB': { language: 'en-GB', voice: 'en-GB-Neural2-A' },
    'de': { language: 'de-DE', voice: 'de-DE-Neural2-A' },
    'fr': { language: 'fr-FR', voice: 'fr-FR-Neural2-A' },
    'es': { language: 'es-ES', voice: 'es-ES-Neural2-A' },
};

// Helper function to strip HTML tags
const stripHtml = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

// Module-level Set to track which contentIds are currently generating TTS
// This survives component remounts and prevents duplicate requests
const generatingContentIds = new Set<string>();

export const TTSAudioPlayer: React.FC<TTSAudioPlayerProps> = ({
    contentId,
    pageId,
    contentType = 'post',
    textContent,
    shouldAutoPlay = false,
    isNewMessage = false
}) => {
    const ttsConfig = useTTSConfig();
    const { t, i18n } = useTranslation();
    const audioRef = useRef<HTMLAudioElement>(null);
    // Generate a unique ID for this component instance to scope CSS
    const uniqueId = useId();
    const progressBarClass = `audio-progress-bar-${uniqueId.replace(/:/g, '')}`;
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState<string>('');
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [isSeeking, setIsSeeking] = useState(false);
    const [shouldAutoPlayInternal, setShouldAutoPlayInternal] = useState(false);
    const [mobileAudioUnlocked, setMobileAudioUnlocked] = useState(false);
    const hasAutoPlayedRef = useRef(false); // Track if we've already auto-played for this message
    const generateAudioRef = useRef<(() => Promise<void>) | null>(null); // Ref to hold generateAudio function

    // Determine if current language is RTL
    const isRTL = RTL_LANGUAGES.includes(i18n.language);

    // Reset refs when contentId changes
    useEffect(() => {
        hasAutoPlayedRef.current = false;
    }, [contentId]);

    // Clean up audio when component unmounts
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                // Clear src to free up memory and prevent any lingering playback
                audioRef.current.removeAttribute('src');
                audioRef.current.load();
            }
        };
    }, []);

    // Set up audio event listeners
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleTimeUpdate = () => {
            // Only update currentTime if user is not seeking (dragging slider)
            if (!isSeeking) {
                setCurrentTime(audio.currentTime);
            }
        };

        const handleLoadedMetadata = () => {
            setDuration(audio.duration);
        };

        const handleCanPlay = () => {
            if (shouldAutoPlayInternal) {
                setShouldAutoPlayInternal(false);

                // Attempt to play
                const playPromise = audio.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            setIsPlaying(true);
                        })
                        .catch(() => {
                            // On mobile, autoplay may fail - that's ok, user can tap again
                            setIsPlaying(false);
                        });
                }
            }
        };

        const handleEnded = () => {
            setIsPlaying(false);
            // Ensure the slider reaches the end
            const finalTime = audio.duration;
            setCurrentTime(finalTime);
            audio.currentTime = finalTime;
        };

        const handleError = (e: Event) => {
            const audioElement = e.target as HTMLAudioElement;
            const error = audioElement.error;

            let errorMessage = t('tts_failed_to_load');

            if (error) {
                switch (error.code) {
                    case error.MEDIA_ERR_ABORTED:
                        errorMessage = t('tts_playback_aborted');
                        break;
                    case error.MEDIA_ERR_NETWORK:
                        // Check if it might be a certificate issue
                        errorMessage = t('tts_network_error');
                        break;
                    case error.MEDIA_ERR_DECODE:
                        errorMessage = t('tts_decode_error');
                        break;
                    case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                        errorMessage = t('tts_format_not_supported');
                        break;
                    default:
                        errorMessage = t('tts_failed_to_load');
                }
            }

            setError(errorMessage);
            setIsPlaying(false);
            setShouldAutoPlayInternal(false);
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('canplay', handleCanPlay);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
        };
    }, [audioUrl, isSeeking, shouldAutoPlayInternal, t]);

    const generateAudio = useCallback(async () => {

        // Prevent duplicate requests (survives component remounts)
        if (generatingContentIds.has(contentId)) {
            return;
        }
        generatingContentIds.add(contentId);

        setIsLoading(true);
        setError(null);
        setLoadingMessage(t('tts_detecting_language'));

        try {
            // Check if TTS config is loaded
            if (!ttsConfig) {
                throw new Error(t('tts_config_not_loaded'));
            }

            // Default language configuration (Arabic)
            let detectedLanguageConfig = {
                language: 'ar-XA',
                voice: 'ar-XA-Standard-A'
            };

            // Detect language if text content is provided
            if (textContent) {
                try {
                    const plainText = stripHtml(textContent);
                    const sampleText = plainText.slice(0, 500); // Use first 500 chars for detection

                    const languageResponse = await fetch(`${API_BASE}/ua/translate/ai-chat-string`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({ string: sampleText }),
                    });

                    if (languageResponse.ok) {
                        const detectedLanguage = await languageResponse.json();
                        const langCode = detectedLanguage[0]?.[0];
                        if (langCode && LANGUAGE_TTS_CONFIG[langCode]) {
                            if (langCode === 'en') {
                                detectedLanguageConfig = {
                                    language: ttsConfig.language,
                                    voice: ttsConfig.voice
                                }
                            } else {
                                detectedLanguageConfig = LANGUAGE_TTS_CONFIG[langCode];
                            }
                        }
                    }
                } catch (langErr) {
                }
            }

            setLoadingMessage(t('tts_generating_audio'));
            // Build request body using API config but override language and voice with detected ones
            const body: any = {
                provider: ttsConfig.provider,
                model: ttsConfig.model,
                voice: ttsConfig.provider === "google" ? detectedLanguageConfig.voice : ttsConfig.voice,
                language: ttsConfig.provider === "google" ? detectedLanguageConfig.language : undefined,
                format: ttsConfig.format,
                contentId,
                contentType,
            };

            if (pageId) {
                body.pageId = pageId;
            }

            const response = await fetch(`${API_BASE}/ua/tts/generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies for authentication
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            let data: TTSResponse = await response.json();

            if (data.success && data.status === 'pending' && data.jobId) {
                data = await pollTtsJob(data.jobId);
            }

            if (data.success && data.data?.audioUrl) {
                setLoadingMessage(t('tts_loading_audio'));
                setDuration(data.data.duration);
                setShouldAutoPlayInternal(true); // Flag that we want to auto-play

                // Set the audio URL which will trigger the useEffect below
                setAudioUrl(data.data.audioUrl);
            } else {
                throw new Error(data.error || 'Failed to generate audio');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to generate audio');
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
            generatingContentIds.delete(contentId);
        }
    }, [ttsConfig, textContent, contentId, contentType, pageId, t]);

    // Keep generateAudio ref updated
    generateAudioRef.current = generateAudio;

    // Auto-play effect when shouldAutoPlay prop is true for new messages
    useEffect(() => {
        if (shouldAutoPlay && isNewMessage && !hasAutoPlayedRef.current && !audioUrl && !isLoading && textContent) {
            hasAutoPlayedRef.current = true;

            // Show loading state immediately while waiting for content to complete
            setIsLoading(true);
            setLoadingMessage(t('tts_preparing', 'Preparing audio'));

            // delay to ensure the text content is loaded
            setTimeout(() => {
                generateAudioRef.current?.();
            }, 3000);
        }
    }, [shouldAutoPlay, isNewMessage, audioUrl, isLoading, textContent, contentId, t]);

    const togglePlay = async () => {
        // On mobile, mark as unlocked on first tap (user gesture)
        if (isMobileDevice() && !mobileAudioUnlocked) {
            setMobileAudioUnlocked(true);
        }

        // If no audio URL, generate it first
        if (!audioUrl) {
            await generateAudio();
            return;
        }

        const audio = audioRef.current;
        if (!audio) {
            return;
        }

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
        } else {
            // Always set src directly if needed (don't rely on React re-render)
            if (!audio.src || !audio.src.startsWith('http')) {
                audio.src = audioUrl;
            }

            // If audio is not ready yet, load and set flag to play when ready
            if (audio.readyState < 3) {
                audio.load();
                setShouldAutoPlayInternal(true);
                return;
            }

            // If audio has ended, reset to start
            if (audio.duration && audio.currentTime >= audio.duration) {
                audio.currentTime = 0;
                setCurrentTime(0);
            }

            // Play the audio directly (we're in a user gesture context)
            const playPromise = audio.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        setIsPlaying(true);
                    })
                    .catch(() => {
                        setError(t('tts_playback_failed'));
                        setIsPlaying(false);
                    });
            } else {
                setIsPlaying(true);
            }
        }
    };

    const handleSeekStart = () => {
        setIsSeeking(true);
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTime = parseFloat(e.target.value);
        setCurrentTime(newTime);
    };

    const handleSeekEnd = (e: React.ChangeEvent<HTMLInputElement>) => {
        const audio = audioRef.current;
        if (!audio) return;
        const newTime = parseFloat(e.target.value);
        audio.currentTime = newTime;
        setCurrentTime(newTime);
        setIsSeeking(false);
    };

    const formatTime = (time: number) => {
        if (!isFinite(time)) return '0:00';
        const mins = Math.floor(time / 60);
        const secs = Math.floor(time % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Calculate progress percentage, ensuring it reaches exactly 100%
    const progressPercentage = useMemo(() => {
        if (!duration || duration === 0) return 0;
        const percentage = (currentTime / duration) * 100;
        // Clamp between 0 and 100 and handle edge case where it's very close to the end
        return Math.min(100, Math.max(0, percentage));
    }, [currentTime, duration]);

    // On mobile, don't set src until user interacts (to avoid Safari's preload restrictions)
    const shouldSetAudioSrc = !isMobileDevice() || mobileAudioUnlocked;

    return (
        <Box width="100%" paddingY="2" dir={isRTL ? 'rtl' : 'ltr'}>
            <audio
                ref={audioRef}
                src={shouldSetAudioSrc ? (audioUrl || undefined) : undefined}
                preload={shouldSetAudioSrc ? "metadata" : "none"}
                playsInline
            />

            <HStack gap="2" width="100%">
                {isLoading ? (
                    <>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            minWidth="32px"
                            height="32px"
                        >
                            <Spinner
                                size="sm"
                                color="var(--cair-accent)"
                            />
                        </Box>
                        <HStack flex="1" gap="2">
                            <style>{`
                                @keyframes dotFlashing {
                                    0% { opacity: 0.2; }
                                    50% { opacity: 1; }
                                    100% { opacity: 0.2; }
                                }
                                .loading-dots {
                                    display: inline-block;
                                }
                                .loading-dots span {
                                    animation: dotFlashing 1.4s infinite;
                                    display: inline-block;
                                    margin-left: 2px;
                                    font-size: 1.2em;
                                    font-weight: bold;
                                }
                                .loading-dots span:nth-child(2) {
                                    animation-delay: 0.2s;
                                }
                                .loading-dots span:nth-child(3) {
                                    animation-delay: 0.4s;
                                }
                            `}</style>
                            <Text fontSize="xs" color="var(--cair-text-secondary)" fontWeight="medium">
                                {loadingMessage}
                                <span className="loading-dots">
                                    <span>.</span>
                                    <span>.</span>
                                    <span>.</span>
                                </span>
                            </Text>
                            <Box flex="1" />
                        </HStack>
                    </>
                ) : (
                    <>
                        {/* Add pulse animation style when audio is ready and waiting for tap */}
                        {audioUrl && !isPlaying && currentTime === 0 && (
                            <style>{`
                                @keyframes pulsePlay {
                                    0%, 100% { transform: scale(1); opacity: 1; }
                                    50% { transform: scale(1.1); opacity: 0.8; }
                                }
                                .pulse-play-btn {
                                    animation: pulsePlay 1.5s ease-in-out infinite;
                                }
                            `}</style>
                        )}
                        <IconButton
                            aria-label={isPlaying ? t('tts_pause') : t('tts_play')}
                            onClick={togglePlay}
                            size="sm"
                            variant="ghost"
                            disabled={isLoading}
                            color={isPlaying || (audioUrl && !isPlaying) ? 'var(--cair-accent)' : 'gray.500'}
                            background="transparent"
                            _hover={{ background: 'var(--cair-accent-subtle)' }}
                            className={audioUrl && !isPlaying && currentTime === 0 ? 'pulse-play-btn' : ''}
                        >
                            {isPlaying ? <LuPause /> : <LuPlay />}
                        </IconButton>
                    </>
                )}

                {!isLoading && audioUrl && (
                    <>
                        <Box flex="1" position="relative">
                            <style>{`
                                .${progressBarClass} {
                                    -webkit-appearance: none;
                                    appearance: none;
                                    width: 100%;
                                    height: 4px;
                                    background: transparent;
                                    border-radius: 2px;
                                    outline: none;
                                    cursor: pointer;
                                    margin-bottom: 8px;
                                    direction: ltr;
                                }

                                /* Track - the background line */
                                .${progressBarClass}::-webkit-slider-runnable-track {
                                    width: 100%;
                                    height: 4px;
                                    background: linear-gradient(
                                        to right,
                                        var(--cair-accent) 0%,
                                        var(--cair-accent) ${progressPercentage}%,
                                        #e2e8f0 ${progressPercentage}%,
                                        #e2e8f0 100%
                                    );
                                    border-radius: 2px;
                                }

                                .${progressBarClass}::-moz-range-track {
                                    width: 100%;
                                    height: 4px;
                                    background: #e2e8f0;
                                    border-radius: 2px;
                                }

                                /* Thumb - the draggable circle */
                                .${progressBarClass}::-webkit-slider-thumb {
                                    -webkit-appearance: none;
                                    appearance: none;
                                    width: 12px;
                                    height: 12px;
                                    background: var(--cair-accent);
                                    border-radius: 50%;
                                    cursor: pointer;
                                    margin-top: -4px;
                                    transition: transform 0.1s ease;
                                }

                                .${progressBarClass}::-moz-range-thumb {
                                    width: 12px;
                                    height: 12px;
                                    background: var(--cair-accent);
                                    border: none;
                                    border-radius: 50%;
                                    cursor: pointer;
                                    transition: transform 0.1s ease;
                                }

                                .${progressBarClass}::-webkit-slider-thumb:hover {
                                    transform: scale(1.2);
                                }

                                .${progressBarClass}::-moz-range-thumb:hover {
                                    transform: scale(1.2);
                                }

                                .${progressBarClass}::-moz-range-progress {
                                    background: var(--cair-accent);
                                    height: 4px;
                                    border-radius: 2px 0 0 2px;
                                }
                            `}</style>
                            <input
                                type="range"
                                className={progressBarClass}
                                min={0}
                                max={duration || 100}
                                step={0.01}
                                value={currentTime}
                                onMouseDown={handleSeekStart}
                                onTouchStart={handleSeekStart}
                                onChange={handleSeek}
                                onMouseUp={(e) => handleSeekEnd(e as any)}
                                onTouchEnd={(e) => handleSeekEnd(e as any)}
                            />
                        </Box>
                        <Text fontSize="xs" color="gray.500" minWidth="45px">
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </Text>
                        <IconButton
                            aria-label={t('download_audio', 'Download audio')}
                            title={t('download_audio', 'Download audio')}
                            variant="ghost"
                            size="xs"
                            color="gray.500"
                            _hover={{ color: 'var(--cair-accent)' }}
                            onClick={() => {
                                // Extension follows the format actually requested — the
                                // circle may be configured for wav or ogg, not mp3.
                                downloadUrl(audioUrl, `${contentId}.${ttsConfig?.format || 'mp3'}`)
                                    .catch(() => setError(t('download_error', 'Something went wrong. Please try again.')))
                            }}
                        >
                            <LuDownload />
                        </IconButton>
                    </>
                )}

                {!audioUrl && !isLoading && !error && (
                    <Text fontSize="xs" color="gray.500">
                        {t('tts_click_to_generate')}
                    </Text>
                )}

                {/* Show "tap to play" message on mobile when audio URL exists (ready or not) */}
                {audioUrl && !isPlaying && !isLoading && !error && currentTime === 0 && isMobileDevice() && (
                    <Text fontSize="xs" color="var(--cair-text-secondary)" fontWeight="medium">
                        {t('tts_tap_to_play_mobile', 'Tap ▶ to play')}
                    </Text>
                )}

                {!isLoading && error && (
                    <Text fontSize="xs" color="red.500">
                        {error}
                    </Text>
                )}
            </HStack>
        </Box>
    );
};

export default TTSAudioPlayer;
